import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  ArrowRight,
  Wifi,
  Zap,
  Shield,
  Settings,
  Globe,
  Lock,
} from "lucide-react";
import { scrollToTop } from "../lib/utils";

interface Benefit {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const Fiber: React.FC = () => {
  const benefits: Benefit[] = [
    {
      icon: <Zap className="h-8 w-8" />,
      title: "Contrôle Total",
      description:
        "Prenez le contrôle absolu de votre infrastructure réseau avec notre solution Dark Fibre sur mesure.",
    },
    {
      icon: <Globe className="h-8 w-8" />,
      title: "Connexion Dédiée",
      description:
        "Bénéficiez d'une connexion point à point dédiée, construite selon vos besoins spécifiques.",
    },
    {
      icon: <Settings className="h-8 w-8" />,
      title: "Votre Équipement",
      description:
        "Utilisez votre propre équipement et définissez vous-même les règles du jeu de votre réseau.",
    },
    {
      icon: <Shield className="h-8 w-8" />,
      title: "Bande Passante Exclusive",
      description:
        "Exploitez une bande passante exclusivement réservée à vos activités, sans compromis.",
    },
    {
      icon: <Lock className="h-8 w-8" />,
      title: "Sécurité Maximale",
      description:
        "Profitez d'un niveau de sécurité inégalé avec votre infrastructure dédiée et isolée.",
    },
    {
      icon: <Wifi className="h-8 w-8" />,
      title: "Performance Optimale",
      description:
        "Déterminez vous-même la capacité de transfert et optimisez les performances selon vos besoins.",
    },
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-blue-900 via-indigo-900 to-purple-900 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/40"></div>
          <img
            src={`${process.env.PUBLIC_URL}/photo-1544197150-b99a580bb7a8.jpg`}
            alt="Fiber optic network"
            className="w-full h-full object-cover opacity-60"
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge
                variant="outline"
                className="mb-4 text-blue-400 border-blue-400"
              >
                Division Spécialisée
              </Badge>
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                Fibre urbaine
              </h1>
              <p className="text-xl text-gray-300 mb-4 font-medium">
                Une division de PaveMark
              </p>
              <p className="text-2xl text-blue-400 font-semibold mb-8">
                Votre réseau, propulsé par la fibre.
              </p>
              <Button
                asChild
                size="lg"
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white text-lg px-8 py-6 shadow-xl"
              >
                <Link
                  to="/contact"
                  onClick={() =>
                    scrollToTop()
                  }
                  className="flex items-center gap-2"
                >
                  Soumission
                  <ArrowRight className="h-5 w-5" />
                </Link>
              </Button>
            </div>

            <div className="relative">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <img
                  src={`${process.env.PUBLIC_URL}/photo-1558494949-ef010cbdcc31.jpg`}
                  alt="Fibre urbaine installation"
                  className="w-full h-64 object-cover rounded-xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
              L'indépendance absolue pour votre réseau
            </h2>

            <div className="space-y-6 text-lg text-gray-600 leading-relaxed text-left">
              <p>
                Il est temps de{" "}
                <strong>
                  prendre le contrôle total de votre infrastructure
                </strong>
                . Si vous recherchez une indépendance absolue sur votre réseau,
                notre solution Dark Fibre est faite pour vous. Vous avez la
                gestion, nous fournissons la fondation.
              </p>

              <p>
                Nous installons la fibre, mais c'est{" "}
                <strong>vous qui définissez les règles du jeu</strong>. De
                l'activation à la gestion complète, votre équipement, vos
                décisions. Vous devenez maître de votre réseau, sans compromis.
              </p>

              <p>
                Avec une <strong>connexion dédiée point à point</strong>,
                construite sur mesure selon vos besoins, vous déterminez la
                capacité de transfert et exploitez une bande passante
                exclusivement réservée à vos activités. C'est bien plus qu'un
                simple service, c'est{" "}
                <strong>la clé de votre liberté numérique</strong>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge
              variant="outline"
              className="mb-4 text-blue-600 border-blue-600"
            >
              Avantages Fibre
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Pourquoi choisir notre solution ?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Découvrez tous les avantages d'une infrastructure fibre dédiée et
              sur mesure
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit: Benefit, index: number) => (
              <Card
                key={index}
                className="group hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-0 shadow-lg"
              >
                <CardHeader className="pb-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-3 bg-gradient-to-br from-blue-100 to-purple-100 rounded-xl text-blue-600 group-hover:from-blue-600 group-hover:to-purple-600 group-hover:text-white transition-all">
                      {benefit.icon}
                    </div>
                  </div>
                  <CardTitle className="text-xl group-hover:text-blue-600 transition-colors">
                    {benefit.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600 leading-relaxed">
                    {benefit.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <Badge
                variant="outline"
                className="mb-4 text-blue-600 border-blue-600"
              >
                Caractéristiques Techniques
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
                Une infrastructure à la hauteur de vos ambitions
              </h2>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Zap className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      Installation Professionnelle
                    </h3>
                    <p className="text-gray-600">
                      Installation complète de l'infrastructure fibre par notre
                      équipe d'experts certifiés.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Settings className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      Gestion Autonome
                    </h3>
                    <p className="text-gray-600">
                      Contrôle total de votre équipement réseau et de sa
                      configuration selon vos besoins.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Globe className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      Connectivité Sur Mesure
                    </h3>
                    <p className="text-gray-600">
                      Connexions point à point dédiées, adaptées précisément à
                      vos exigences d'entreprise.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-6">
                  <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-6 rounded-2xl border border-blue-100">
                    <img
                      src={`${process.env.PUBLIC_URL}/photo-1573164713714-d95e436ab8d6.jpg`}
                      alt="Infrastructure technique"
                      className="w-full h-32 object-cover rounded-lg mb-4"
                    />
                    <h4 className="font-semibold text-gray-900">
                      Équipement Réseau
                    </h4>
                  </div>
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-2xl border border-green-100">
                    <img
                      src={`${process.env.PUBLIC_URL}/photo-1558494949-ef010cbdcc31.jpg`}
                      alt="Installation fibre"
                      className="w-full h-32 object-cover rounded-lg mb-4"
                    />
                    <h4 className="font-semibold text-gray-900">
                      Installation Fibre
                    </h4>
                  </div>
                </div>
                <div className="space-y-6 mt-12">
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-2xl border border-purple-100">
                    <img
                      src={`${process.env.PUBLIC_URL}/photo-1544197150-b99a580bb7a8.jpg`}
                      alt="Connectivité réseau"
                      className="w-full h-32 object-cover rounded-lg mb-4"
                    />
                    <h4 className="font-semibold text-gray-900">
                      Connectivité
                    </h4>
                  </div>
                  <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-6 rounded-2xl border border-amber-100">
                    <img
                      src={`${process.env.PUBLIC_URL}/photo-1551808525-51a94da548ce.jpg`}
                      alt="Support technique"
                      className="w-full h-32 object-cover rounded-lg mb-4"
                    />
                    <h4 className="font-semibold text-gray-900">
                      Support Expert
                    </h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Prêt à révolutionner votre infrastructure ?
          </h2>
          <p className="text-xl text-blue-100 mb-8 leading-relaxed">
            Contactez notre équipe spécialisée pour découvrir comment notre
            solution Dark Fibre peut transformer votre connectivité d'entreprise
            et vous donner l'indépendance totale que vous méritez.
          </p>
          <Button
            asChild
            size="lg"
            variant="outline"
            className="bg-white text-blue-600 border-white hover:bg-blue-50 text-lg px-8 py-6 shadow-xl"
          >
            <Link
              to="/contact"
              onClick={() => scrollToTop()}
              className="flex items-center gap-2"
            >
              Demander une consultation
              <ArrowRight className="h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Fiber;
